import {useState,useMemo,useEffect} from 'react'
import {useDispatch} from 'react-redux'
import {
	useFilteredMetricProgressData,
	useMetricProgressLogger
} from './hooks'
import {
	deleteProgressStat,
	editMetricStat
} from './slice'
import DatePicker from 'react-datepicker'
import InputGroup from 'react-bootstrap/InputGroup'
import Spinner from 'react-bootstrap/Spinner'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import LocaleDate from '../LocaleDate/LocaleDate'
import { Line } from 'react-chartjs-2'
import {dateToYMD} from '../../util/datetime'
import { useDebounceCallback } from '@react-hook/debounce'
import DataInput from './DataInput'
import {secondsToTimeObj} from '../../util/datetime'
import css from './Metrics.module.css'

const Stat = ({stat})=>{
	const dispatch = useDispatch()
	const [localValue,setLocalValue] = useState(stat.value)
	const [localNotes,setLocalNotes] = useState(stat.notes)
	const [localDate,setLocalDate] = useState(stat.date)

	const {statusLog} = useMetricProgressLogger()

	useEffect(()=>{
		setLocalValue(stat.value)
		setLocalNotes(stat.notes)
	},[stat])

	const debouncedLog = useDebounceCallback(statusLog,1000)

	const handleValueChange = value => {
		setLocalValue(value)
		const payload = {
			overwrite_existing:true,
			metric_id:stat.metric_id,
			date:localDate,
			notes:stat.notes,
			value
		}
		//time units require special handling
		if (stat.unit_id === 2) {
			const timeObj = secondsToTimeObj(value)
			payload.hours = timeObj.hours
			payload.minutes = timeObj.minutes
			payload.seconds = timeObj.seconds
		} else {
			payload.value = value
		}
		debouncedLog(payload)
	}	

	const handleNotesChange = e => {
		const notes = e.target.value
		setLocalNotes(notes)
		const payload = {
			overwrite_existing:true,
			metric_id:stat.metric_id,
			date:localDate,
			value:stat.value,
			notes
		}
		//time units require special handling
		if (stat.unit_id === 2) {
			const timeObj = secondsToTimeObj(stat.value)
			payload.hours = timeObj.hours
			payload.minutes = timeObj.minutes
			payload.seconds = timeObj.seconds
		} else {
			payload.value = stat.value
		}
		debouncedLog(payload)
	}	

	const dispatchEditMetricStat = (payload)=>{
		dispatch(
			editMetricStat(payload)
		)
	}

	const debouncedDispatchEditMetricStat = useDebounceCallback(
		dispatchEditMetricStat,
		1000
	)
	
	const handleDateChange = selected => {
		const value = stat.value
		const overwriteExisting = true
		const notes = stat.notes
		setLocalDate(dateToYMD(selected))
		const date = dateToYMD(selected)
		const metricId = stat.metric_id
		debouncedDispatchEditMetricStat({
			stat_id:stat.stats_id,
			value,
			date,
			notes
		})
	}	

	const handleRemove = ()=>{
		if (!window.confirm('Are you sure you want to delete this metric progress data?')) return
		dispatch(deleteProgressStat({
			id:stat.stats_id,
			metricId:stat.metric_id
		}))
	}

	return (
		<div className="d-flex mt-1 align-items-start">
			<DatePicker
				onChange={handleDateChange}
				selected={new Date(`${localDate}T00:00:00`)}
				customInput={<Form.Control style={{width:'150px',textAlign:'center'}} />}
				className="mr-1"
			/>
			<DataInput 
				onChange={handleValueChange}
				value={localValue}
				unit={stat.unit_id}
			/>
			<Form.Control
				onChange={handleNotesChange}
				style={{minHeight:'38px',height:'38px'}}
				placeholder="notes"
				className="ml-1"
				as="textarea"
				value={localNotes}
				
			/>
			<Button onClick={handleRemove} className="ml-1" variant="outline-danger" >
				<i className="fa fa-times"/>
			</Button>
		</div>
	)
}

const ProgressTable = ({id})=>{
	const data = useFilteredMetricProgressData(id)

	const {stats} = data
	const labels = stats.map(d=>d.date)
	const chartData = stats.map(d=>d.value)
	const r = ~~(100 * Math.random() - 25)
	//memoize the colors
	const borderColor = useMemo(()=>`hsla(${213-r}, 100%, 60%,0.8)`,[])
	const backgroundColor = useMemo(()=>`hsla(${213-r}, 100%, 60%,0.4)`,[])

	const {log:logProgress} = useMetricProgressLogger()

	const [addingData,setAddingData] = useState(false)
	const handleAddData = ()=>{
		setAddingData(true)
		const metricId = id
		let date = new Date()
		if(stats.length>0){
			//get latest date
			date = new Date(`${stats[stats.length - 1].date}T00:00:00`)
			//+1 day to latest date
			date.setDate(date.getDate() + 1)
		}
		
		const value = 0
		const notes = ''
		const payload = {
			metric_id:metricId,
			date:dateToYMD(date),
			value,
			notes
		}
		logProgress(payload).then(()=>{
			setAddingData(false)
		})
	}

	return (
		<div className={css.NoBreak}>
			<div className="d-flex flex-wrap mb-2">
				<div>
					<strong style={{display:'inline-block',paddingBottom:'2px',marginBottom:'2px',borderBottom:'1px solid #ccc'}}>
						{data.assessment_name} {data.unit !== 'None'&&`(${data.unit})`}
					</strong>
					{stats.length>0&&
					<div className="d-flex mt-auto align-items-start">
						<strong style={{width:'100px'}}>Date</strong>
						<strong 
							style={{width:'80px'}} 
							className="ml-1"
						>
							Value
						</strong>
					</div>
				}
				</div>
				{/* Only display if 2 or more data points */}
				{stats.length>1&&
					<div className="rounded border ml-auto" style={{height:'52px',width:'102px',overflow:'hidden'}}>
						<Line
							width={100}
							height={50}
							options={{
								title:{display:false},
								legend:{
									display:false
								},
								tooltips:{
									enabled:false,
								},
								animation: {
									duration: 0
								},
								maintainAspectRatio:false,
								responsive:false,
								scales:{
									xAxes:[{
										display:false
									}],
									yAxes:[{
										display:false
									}],
								}
							}}
							data={
								{
									labels,
									datasets: [{
										lineTension: 0,
										label: data.assessment_name,
										backgroundColor,
										borderColor,
										data:chartData
									}]
								}
							}
						/>
					</div>
				}
			</div>
			{stats.length===0&&
				<small className="d-block text-center text-muted">No data</small>
			}
			{stats.map( stat => <Stat key={stat.stats_id} stat={stat}/> )}
			<Button
				disabled={addingData}
				onClick={handleAddData}
				variant="outline-success"
				className={["mt-2",css.NoPrint].join(' ')}
				
				block
			>
				Add Data {addingData?<Spinner animation="border" />:<i className="fa fa-plus"/>}
			</Button>
		</div>
	)
}

export default ProgressTable